import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import MENU from '../config/menu.js';
import BrandLockup from './ui/BrandLockup';
import api from '../services/api.js';

const COLLAPSE_KEY = 'eon-sidebar-collapsed';

function initials(name = '') {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || 'U'
  );
}

/** Collapsible Settings group; auto-opens when a child route is active. */
function NavGroup({ item, collapsed, onExpandRail }) {
  const location = useLocation();
  const childActive = item.children.some((c) => location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const toggle = () => {
    if (collapsed) onExpandRail();
    setOpen((o) => !o);
  };

  return (
    <div className={`nav-group${open ? ' open' : ''}`}>
      <div
        className="nav-group-toggle side-nav-anchor"
        role="button"
        tabIndex={0}
        title={item.label}
        onClick={toggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggle())}
      >
        <span className="tg-left" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="material-icons">{item.icon}</span>
          <span className="label-text">{item.label}</span>
        </span>
        <span className="chevron material-icons">chevron_right</span>
      </div>
      <div className="nav-group-children">
        {item.children.map((c) => (
          <NavLink key={c.to} to={c.to} className="nav-link" title={c.label}>
            <span className="label-text">{c.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout, isAdmin } = useAuth();
  const [badges, setBadges] = useState({});
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Sync the collapsed class onto <body> (the SCSS keys off body.sidebar-collapsed).
  useEffect(() => {
    document.body.classList.toggle('sidebar-collapsed', collapsed);
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Contractor accounts waiting for a credit decision (refreshed on navigation).
  useEffect(() => {
    if (!isAdmin) return;
    api.requestEnvelope('/credit-approvals', { method: 'GET', query: { status: 'pending_approval' } })
      .then((p) => setBadges({ creditPending: p?.counts?.pending_approval ?? 0 }))
      .catch(() => {});
  }, [isAdmin, location.pathname]);

  const toggleSidebar = () => {
    if (window.matchMedia('(max-width: 992px)').matches) {
      setDrawerOpen((o) => !o);
    } else {
      setCollapsed((c) => !c);
    }
  };

  // Field Sales users belong in the field app.
  if (user?.role === 'Field Sales') return <Navigate to="/field" replace />;
  if (user?.role === 'Contractor') return <Navigate to="/portal" replace />;

  return (
    <>
      <aside className={`app-sidebar${drawerOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <BrandLockup endorse className="header-logo brand-logo-full" />
          <img src="/assets/salesorderos-icon.svg" alt="SalesOrderOS" className="brand-logo-icon" />
        </div>
        <div className="sidebar-user">
          <div className="user-name">{user?.name || 'User'}</div>
          <div className="user-role">{user?.role || ''}</div>
        </div>
        <nav className="nav">
          {MENU.filter((item) => !item.adminOnly || isAdmin).map((item) =>
            item.children ? (
              <NavGroup
                key={item.label}
                item={item}
                collapsed={collapsed}
                onExpandRail={() => setCollapsed(false)}
              />
            ) : (
              <NavLink key={item.to} to={item.to} className="nav-link" title={item.label}>
                <span className="material-icons">{item.icon}</span>
                <span className="label-text">{item.label}</span>
                {item.badge && badges[item.badge] > 0 && <span className="nav-badge" aria-label={`${badges[item.badge]} pending`}>{badges[item.badge]}</span>}
              </NavLink>
            )
          )}
        </nav>
      </aside>

      <header className="app-header">
        <div className="header-context-wrap">
          <button className="sidebar-toggle" type="button" aria-label="Toggle navigation" onClick={toggleSidebar}>
            <span className="material-icons">menu</span>
          </button>
          <span className="header-context">Sales Operations</span>
        </div>
        <div className="header-user">
          <span className="header-greeting">
            Hi, {user?.first_name || user?.name || 'User'}
          </span>
          <span className="header-avatar">{initials(user?.name)}</span>
          <button className="header-logout" type="button" title="Logout" onClick={logout}>
            <span className="material-icons">logout</span>
          </button>
        </div>
      </header>

      <main className="app-main">
        <div className="content-area">
          <Outlet />
        </div>
      </main>
    </>
  );
}
